import { shuffleArray } from './array';
import { buildGtmCallback, pushGtmEvent } from './gtm';
import rollbar from './rollbar-agent';

/**
 * Shared logic for the "Randomization" Convert A/B test on the donation
 * form's source code (a.k.a "What motivated your contribution today?")
 * field. Used by both the address step (to display the field) and the
 * donation form (to report on it once the payment step is submitted).
 */

// Module-level cache so the shuffled order is computed once per checkout
// form (page load) no matter how many times/places it's read from.
let cachedShuffledSourceCodesKey = null;
let cachedShuffledSourceCodes = null;

/**
 * Returns the current "Randomization" Convert A/B test variant ('A' or 'B'),
 * or null when the visitor isn't part of that test.
 */
export const getRandomizationTestVersion = () => {
  const testVersion = typeof window !== 'undefined' ? window.cwConvertVariant : undefined;

  return testVersion === 'A' || testVersion === 'B' ? testVersion : null;
};

/**
 * Returns sourceCodes in random order, only for variant B of the
 * "Randomization" Convert A/B test. Otherwise returns sourceCodes unchanged.
 */
export const getSourceCodesToDisplay = (sourceCodes) => {
  if (getRandomizationTestVersion() !== 'B') {
    return sourceCodes;
  }

  const key = sourceCodes.map((sourceCode) => sourceCode.value).join('|');
  if (key !== cachedShuffledSourceCodesKey) {
    cachedShuffledSourceCodesKey = key;
    cachedShuffledSourceCodes = shuffleArray(sourceCodes);
  }

  return cachedShuffledSourceCodes;
};

/**
 * A source code option is considered the "Other" option based on its label
 * (e.g. "Other", "Other reason", "Any other reason") — there's no dedicated
 * machine value for it, since source codes are editor-managed CRM campaigns.
 */
const isOtherSourceCodeOption = (sourceCode) =>
  typeof sourceCode?.label === 'string' && sourceCode.label.trim().toLowerCase().includes('other');

// Set by the donation form on render. The submit saga (shared by card,
// PayPal, Apple Pay and Google Pay) reads it, as the external payment buttons
// don't go through the form's own submit handler.
let sourceCodeFieldContext = null;

export const registerSourceCodeField = ({ sourceCodes, wasFieldShown }) => {
  sourceCodeFieldContext = { sourceCodes, wasFieldShown };
};

/**
 * Sends a single GTM event once the payment step is submitted, reporting how
 * the donor interacted with the source code field. Only fires for visitors
 * bucketed into the "Randomization" Convert A/B test (variant A or B), and
 * only when the field was actually shown on the address step.
 */
export const trackSourceCodeFieldSubmitted = (selectedSourceCode) => {
  const testVersion = getRandomizationTestVersion();

  if (!testVersion || !sourceCodeFieldContext?.wasFieldShown) {
    return Promise.resolve();
  }

  const displayedSourceCodes = getSourceCodesToDisplay(sourceCodeFieldContext.sourceCodes);
  const selectedPositionIndex = displayedSourceCodes.findIndex(
    (sourceCode) => sourceCode.value === selectedSourceCode?.value,
  );
  const hasOtherOption = displayedSourceCodes.some(isOtherSourceCodeOption);

  // Without GTM or a connection the callback never fires, don't wait for it.
  const canConfirmDelivery = typeof window.dataLayer === 'object' && navigator.onLine;

  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      if (rollbar) {
        rollbar.error('GTM did not confirm the campaigns randomisation event in time.', {
          convert_variant: testVersion,
          selected_campaign_value: selectedSourceCode?.value ?? null,
          gtm_loaded: Boolean(window.google_tag_manager),
          data_layer_length: window.dataLayer.length,
        });
      }

      resolve();
    }, 1000);

    pushGtmEvent({
      event: 'convert_campaigns_randomisation_test',
      campaigns_randomisation_AB_test: {
        // 1-based position, to match how it's displayed to the donor.
        selected_campaign_position: selectedPositionIndex === -1 ? null : selectedPositionIndex + 1,
        campaigns_count: displayedSourceCodes.length,
        other_option_exists: hasOtherOption,
        other_option_selected: hasOtherOption && isOtherSourceCodeOption(selectedSourceCode),
        convert_variant: testVersion,
        campaign_label:
          selectedPositionIndex === -1 ? null : displayedSourceCodes[selectedPositionIndex].label,
      },
      eventTimeout: 1000,
      eventCallback: buildGtmCallback(() => {
        clearTimeout(timeoutId);
        resolve();
      }),
    });

    if (!canConfirmDelivery) {
      clearTimeout(timeoutId);
      if (rollbar) {
        rollbar.error('GTM did not confirm the campaigns randomisation event in time.', {
          convert_variant: testVersion,
          selected_campaign_value: selectedSourceCode?.value ?? null,
          gtm_loaded: Boolean(window.google_tag_manager),
          data_layer_length: window.dataLayer.length,
        });
      }
      resolve();
    }
  });
};
